import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CustomField } from '@/src/infrastructure/interfaces/IIntegrations.interface';
import { useIntegrationService } from '@/src/infrastructure/Protocol/Integrations/useIntegrationService';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import {
  atomCreateOrEditIntegrationForm,
  atomCurrentStep,
  atomStepCompleted,
} from '@/src/modules/IntegrationsModule/atoms/integrations';
import {
  DEFAULT_INTEGRATION_FORM,
  MAP_DEFAULT_VALUES,
} from '@/src/modules/IntegrationsModule/constants';

import { useApiKeys } from './useApiKeys';
import { CreateOrEditIntegrationForm } from './useCreateOrEditIntegrationForm';
import { useGroups } from './useGroups';
import { useStepsOptions } from './useStepsOptions';

import { FieldsResponse } from '@/modules/ContactsModule/types/Segments';

export const useGetDataIntegration = () => {
  const { id } = useParams();
  const integrationsService = useIntegrationService();
  const service = useService();
  const { optionsStepOne, optionsStepThree } = useStepsOptions();
  const { groupsOptions, createGroup, getGroups } = useGroups();
  const { apiKeysOptions, getApisKeys } = useApiKeys();
  const setStateForm = useSetAtom(atomCreateOrEditIntegrationForm);
  const setCurrentStep = useSetAtom(atomCurrentStep);
  const setStepCompleted = useSetAtom(atomStepCompleted);
  const [isLoading, setIsLoading] = useState(false);
  const [dataIntegrationFormIsLoading, setDataIntegrationFormIsLoading] = useState(false);
  const [nameIntegration, setNameIntegration] = useState('');

  const getIntegrationDetailData = useCallback(
    async (id: number) => {
      setIsLoading(true);
      const response = await integrationsService.getIntegration(id);
      if (response.success) {
        setNameIntegration(response.data.name);
        setIsLoading(false);
        return response.data;
      }
      setIsLoading(false);
    },
    [integrationsService]
  );

  const getCustomFields = useCallback(
    async (customFieldsSaved?: CustomField[]) => {
      if (!service) return [];
      const response = await service.getFieldsFilters();
      if (response.success) {
        const defaultFields = (response.data as FieldsResponse).defaultFields.map((field) => ({
          ...field,
          name: MAP_DEFAULT_VALUES[field.name as keyof typeof MAP_DEFAULT_VALUES] ?? field.name,
          value: MAP_DEFAULT_VALUES[field.name as keyof typeof MAP_DEFAULT_VALUES] ?? field.name,
        }));

        const unmodifiableDefaultFields = [
          'recency',
          'frequency',
          'amount',
          'nps',
          'eCommerceClassification',
        ];

        const customFields: CreateOrEditIntegrationForm['stepTwo'] = [
          ...defaultFields,
          ...(response.data as FieldsResponse).customFields,
        ]
          .filter((field) => !unmodifiableDefaultFields.some((uF) => uF === field.value))
          .map((field) => {
            const isChecked = customFieldsSaved?.some(
              (customField) => customField.name === field.name
            );
            return {
              ...field,
              isChecked: field.name === 'email' || isChecked,
              isDisabled: field.name === 'email',
            };
          });

        return customFields;
      } else {
        return [];
      }
    },
    [service]
  );

  const setIntegrationDetailInStateForm = useCallback(async () => {
    setDataIntegrationFormIsLoading(true);
    const data = await getIntegrationDetailData(Number(id));
    console.log('data: ', data);
    if (data) {
      const [customFields, groupsValue, apiKeyValue] = await Promise.all([
        getCustomFields(data.customFields),
        getGroups(data.addGroupIds ?? []),
        getApisKeys(data.apiKeyId ?? ''),
      ]);
      let currentStep = 1;
      let stepCompleted = 0;
      let stepOne = DEFAULT_INTEGRATION_FORM.stepOne;
      if (data.addBulk !== undefined) {
        stepOne = data.addBulk ? optionsStepOne[1] : optionsStepOne[0];
        currentStep = 2;
        stepCompleted = 1;
      }

      if (data.customFields) {
        currentStep = 3;
        stepCompleted = 2;
      }

      let stepThree = DEFAULT_INTEGRATION_FORM.stepThree;
      if (data.addGroupIds) {
        stepThree = (data.addGroupIds?.length ?? 0) > 0 ? optionsStepThree[1] : optionsStepThree[0];
        currentStep = 4;
        stepCompleted = 3;
      }

      if (data.apiKeyId) {
        currentStep = 0;
        stepCompleted = 4;
      }

      const stateForm: CreateOrEditIntegrationForm = {
        stepOne,
        stepTwo: customFields,
        stepThree,
        stepThreeGroups: groupsValue,
        stepFour: apiKeyValue,
        stepFourSwitch: data.status === 'active' ? true : false,
      };

      setCurrentStep(currentStep);
      setStepCompleted(stepCompleted);
      setStateForm(stateForm);
    }
    setDataIntegrationFormIsLoading(false);
  }, [
    getApisKeys,
    getCustomFields,
    getGroups,
    getIntegrationDetailData,
    id,
    optionsStepOne,
    optionsStepThree,
    setCurrentStep,
    setStateForm,
    setStepCompleted,
  ]);

  return {
    getIntegrationDataIsLoading: isLoading,
    dataIntegrationFormIsLoading,
    setIntegrationDetailInStateForm,
    nameIntegration,
    createGroup,
    groupsOptions,
    apiKeysOptions,
    getIntegrationDetailData,
  };
};
